import { Component, type ErrorInfo, type ReactNode } from 'react';
import { redirectToLogin } from '../auth/redirect';

const AUTH_ERROR_PATTERN = /token|auth|401|unauthorized|interaction_required|login_required/i;

interface State {
  authError: boolean;
  fatalError: Error | null;
}

interface Props {
  children: ReactNode;
}

/// <summary>
/// Top-level boundary around AuthedShell. Catches render-time errors whose
/// message looks auth-related (stale tokenRef access, Query cache surfacing
/// a 401 mid-render, MSAL throws during navigation) and renders a clean
/// "sign in again" screen instead of the React red-screen. Non-auth errors
/// fall through to a generic failure screen.
/// </summary>
export class AuthErrorBoundary extends Component<Props, State> {
  state: State = { authError: false, fatalError: null };

  static getDerivedStateFromError(error: Error): State {
    if (AUTH_ERROR_PATTERN.test(error.message)) {
      return { authError: true, fatalError: null };
    }
    return { authError: false, fatalError: error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AuthErrorBoundary]', error, info.componentStack);
  }

  private onSignIn = () => {
    void redirectToLogin();
  };

  render() {
    if (this.state.authError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="bg-white rounded-lg shadow-md max-w-sm w-full p-6 text-center">
            <h1 className="text-lg font-semibold text-slate-900">Session expired</h1>
            <p className="mt-2 text-sm text-slate-700">
              Please sign in again to continue.
            </p>
            <button
              type="button"
              onClick={this.onSignIn}
              className="mt-5 px-4 py-2 text-sm bg-sky-600 text-white rounded hover:bg-sky-700"
            >
              Sign in
            </button>
          </div>
        </div>
      );
    }
    if (this.state.fatalError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="bg-white rounded-lg shadow-md max-w-md w-full p-6">
            <h1 className="text-lg font-semibold text-slate-900">Something went wrong</h1>
            <p className="mt-2 text-sm text-slate-700">
              An unexpected error occurred. Please reload the page.
            </p>
            <pre className="mt-4 text-xs text-slate-500 whitespace-pre-wrap break-words">
              {this.state.fatalError.message}
            </pre>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
